import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Alert, Spin, Result, Modal } from 'antd';
import { CalendarOutlined, ExclamationCircleOutlined, UserOutlined } from '@ant-design/icons';
import FormBuilder from '../components/form/FormBuilder';
import { publicReportApi } from '../api/publicReportApi';
import { useApi } from '../hooks/useApi';
import { formatDate } from '../utils/dateHelpers';

const { confirm } = Modal;

const PublicReportFill = () => {
  const { token } = useParams();
  const [report, setReport] = useState(null);
  const [editable, setEditable] = useState(false);
  const [memberName, setMemberName] = useState('');
  const [fetchLoading, setFetchLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const { loading, execute } = useApi();

  const loadReport = async () => {
    setFetchLoading(true);
    try {
      const response = await publicReportApi.get(token);
      setReport(response.data.report);
      setEditable(response.data.editable);
      setMemberName(response.data.memberName || '');
    } catch {
      setReport(null);
    } finally {
      setFetchLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleSubmit = (data) => {
    confirm({
      title: 'Отправить отчёт?',
      icon: <ExclamationCircleOutlined />,
      okText: 'Отправить',
      cancelText: 'Отмена',
      onOk: async () => {
        await execute(() => publicReportApi.submit(token, data), {
          successMessage: 'Отчёт сохранён',
          onSuccess: () => {
            setSubmitted(true);
            loadReport();
          },
        });
      },
    });
  };

  if (fetchLoading) {
    return (
      <div className="fill-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!report) {
    return (
      <div className="fill-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Result status="404" title="Отчёт не найден" subTitle="Ссылка недействительна или отчёт удалён" />
      </div>
    );
  }

  if (report.status === 'expired' || !editable) {
    return (
      <div className="fill-page">
        <header className="fill-header fill-header--fixed">
          <div className="fill-header-inner">
            <h1>Анкета One-to-One</h1>
            <div className="fill-header-meta">
              {memberName && (
                <span className="fill-header-badge">
                  <UserOutlined /> {memberName}
                </span>
              )}
            </div>
          </div>
        </header>
        <div className="fill-header-spacer" aria-hidden="true" />
        <main className="fill-main">
          <Result
            status="info"
            title="Срок заполнения истёк"
            subTitle={`Отчёт для ${memberName} больше недоступен для редактирования.`}
          />
          {report.status === 'submitted' && (
            <Alert
              type="success"
              message="Отчёт был заполнен"
              description={`Дата отправки: ${formatDate(report.submittedAt)}`}
              showIcon
              className="fill-alert"
            />
          )}
        </main>
      </div>
    );
  }

  return (
    <div className="fill-page">
      <header className="fill-header fill-header--fixed">
        <div className="fill-header-inner">
          <h1>Анкета One-to-One</h1>
          <div className="fill-header-meta">
            {memberName && (
              <span className="fill-header-badge">
                <UserOutlined /> {memberName}
              </span>
            )}
            <span className="fill-header-badge">
              <CalendarOutlined /> до {formatDate(report.expiresAt)}
            </span>
          </div>
        </div>
      </header>
      <div className="fill-header-spacer" aria-hidden="true" />

      <main className="fill-main">
        {submitted && (
          <Alert
            type="success"
            message="Отчёт сохранён"
            description="Вы можете вернуться и отредактировать его до истечения срока."
            showIcon
            className="fill-alert"
            closable
            onClose={() => setSubmitted(false)}
          />
        )}

        <FormBuilder
          initialValues={report.status === 'submitted' ? report : undefined}
          onSubmit={handleSubmit}
          loading={loading}
          isEdit={report.status === 'submitted'}
          optional
          variant="public"
        />
      </main>
    </div>
  );
};

export default PublicReportFill;
